import React, { FC, useMemo, useState } from 'react';
import { useNitroPool } from '@app/hooks/nitro/useNitroPool';
import { HIDDEN_NITRO_POOLS } from '@app/constants/common';
import { useConfig } from '@app/config';
import { Vault } from '@app/hooks/plugins/useVaults';
import { useNitroPoolDetails } from '@app/hooks/nitro/useNitroPoolDetails';
import { useStakingPosition } from '@app/hooks/positions/useStakingPosition';
import { useKpNftLockDetails } from '@app/hooks/plugins/useKpNftLockDetails';
import { ListLoader } from '@app/screens/pools/components/ListLoader';
import { MultiStepNitroStake } from '@app/screens/positions/components/MultiStepNitroStake';
import { formatUSD } from '@app/helpers/formatUSD';
import { formatNumber } from '@app/helpers/format';
import { format } from 'date-fns';
import { Box } from '@chakra-ui/react';
import { KpNFTControlsHeader } from '@app/screens/positions/components/SpNftControls/KpNFTControlsHeader';
import { TransactionResultModal } from '@app/components/TransactionResultModal';
import { useLiquidityWizardState } from '@app/screens/liquidity/LiquidityWizard/state';

interface Props {
  vault: Vault;
  tokenId: string;
  deposit: string;
  nftPoolAddress: string;
  nitroPoolId?: string;
}

const NOW = new Date().getTime();

export const StakeToNitroView: FC<Props> = ({
  vault,
  tokenId,
  nftPoolAddress,
  nitroPoolId
}) => {
  const config = useConfig();
  const {
    actions: { reset }
  } = useLiquidityWizardState();
  const [feedback, setFeedback] = useState<{
    status: 'success' | 'error';
    title: string;
    message: string;
    txHash?: string;
  } | null>(null);
  const { data: nitroPool } = useNitroPool(nftPoolAddress, nitroPoolId);

  const activePool = useMemo(() => {
    if (!nitroPool) {
      return null;
    }

    if (
      !HIDDEN_NITRO_POOLS.includes(nitroPool.id) &&
      NOW < +nitroPool.endTime * 1000
    ) {
      return nitroPool;
    }

    return null;
  }, [nitroPool]);

  const {
    data: nitroPoolDetails,
    isLoading,
    tvlUsd,
    totalApr
  } = useNitroPoolDetails(nftPoolAddress);

  const { data } = useStakingPosition({
    tokenId,
    poolAddress: nftPoolAddress
  });

  const lockDetails = useKpNftLockDetails(data);

  const { isLockRequired, reqLockDurationInDays, lockDurationInDays } =
    useMemo(() => {
      if (!nitroPoolDetails || !lockDetails) {
        return {
          isLockRequired: true,
          reqLockDurationInDays: 0,
          lockDurationInDays: lockDetails?.lockDurationInDays
        };
      }

      const reqLockDurationInDays = Math.ceil(
        +nitroPoolDetails.nitroPool.requiredLockDuration / 3600 / 24
      );

      const lockedDuration = lockDetails.lockDurationInDays;

      return {
        isLockRequired: true,
        reqLockDurationInDays,
        lockDurationInDays: lockedDuration
      };
    }, [lockDetails, nitroPoolDetails]);

  function renderContent() {
    if (!activePool || !config?.FLAGS.showNitroPage) {
      return null;
    }

    if (isLoading) {
      return <ListLoader />;
    }

    if (nitroPoolDetails) {
      return (
        <MultiStepNitroStake
          onClose={() => {
            reset();
          }}
          tokenId={tokenId}
          poolAddress={nftPoolAddress}
          onResult={res => setFeedback(res)}
          tokenDetails={vault}
          deposit=""
          reqLockDurationInDays={reqLockDurationInDays}
          lockDurationInDays={lockDurationInDays}
          nitroPoolAddress={activePool!.id}
          token0Symbol={
            nitroPoolDetails?.nitroPool.rewardsToken1Amount !== '0'
              ? nitroPoolDetails?.nitroPool.rewardsToken1?.symbol
              : undefined
          }
          token1Symbol={
            nitroPoolDetails?.nitroPool.rewardsToken2Amount
              ? nitroPoolDetails?.nitroPool.rewardsToken2?.symbol
              : undefined
          }
          tvl={formatUSD.format(tvlUsd ? +tvlUsd : 0)}
          apr={`${formatNumber(totalApr, 2)}%`}
          endTime={new Date(+nitroPoolDetails.nitroPool.endTime * 1000)}
          minimumAmount={
            nitroPoolDetails?.nitroPool.requiredDepositAmount
              ? nitroPoolDetails.nitroPool.requiredDepositAmount || '-'
              : '-'
          }
          minimumLock={
            nitroPoolDetails?.nitroPool.requiredLockDuration &&
            nitroPoolDetails?.nitroPool.requiredLockDuration !== '0'
              ? `${Math.ceil(
                  +nitroPoolDetails.nitroPool.requiredLockDuration / 3600 / 24
                )} days`
              : '-'
          }
          lockedUntil={
            lockDetails.lockedUntilDate
              ? format(lockDetails.lockedUntilDate, 'dd MMM yyyy, hh:mm')
              : '-'
          }
        />
      );
    }

    return null;
  }

  return (
    <Box p="24px" backgroundColor="neutral.800" borderRadius="8px">
      <KpNFTControlsHeader
        tokenDetails={vault}
        tokenId={tokenId}
        title="Stake into a Nitro pool"
        subtitle="Deposit your kpNFT into a Nitro to earn additional yield"
      />
      {renderContent()}
      <TransactionResultModal
        isOpen={!!feedback}
        status={feedback?.status}
        title={feedback?.title}
        message={feedback?.message}
        txHash={feedback?.txHash}
        onClose={() => {
          setFeedback(null);
          reset();
        }}
      />
    </Box>
  );
};
